import { z } from 'zod';

import { API_ROUTES } from '../../../lib/apiRoutes';
import { apiRequest } from '../../../lib/api';
import { validateWithSchema } from '../../shared/utils/apiValidation.util';
import type { AppSettings, FulfillmentModel, PartnerStoreSummary, SystemSettingsResponse } from '../types/system.types';

const healthzResponseSchema = z
  .object({
    status: z.literal('ok')
  })
  .passthrough();

const settingsResponseSchema = z
  .object({
    settings: z.custom<AppSettings>().optional(),
    data: z.custom<AppSettings>().optional()
  })
  .passthrough();

export const systemApi = {
  async healthz(): Promise<void> {
    const endpoint = `${API_ROUTES.v1.health}/healthz`;
    const response = await apiRequest<unknown>(endpoint);
    validateWithSchema(healthzResponseSchema, response, endpoint);
  },
  async settings(): Promise<SystemSettingsResponse> {
    const response = await apiRequest<unknown>(API_ROUTES.v1.settings);
    const parsed = validateWithSchema<z.infer<typeof settingsResponseSchema>>(settingsResponseSchema, response, API_ROUTES.v1.settings);
    return { settings: parsed.settings ?? parsed.data };
  },
  async storefront(): Promise<{
    allowGuestCheckout: boolean;
    checkoutAvailable: boolean;
    maintenanceMode: boolean;
    fulfillmentModel: FulfillmentModel;
    ownStoreInventoryEnabled: boolean;
    shoppingCloseBufferMinutes: number;
    hubShoppingCloseBufferMinutes: number;
    partnerStores: PartnerStoreSummary[];
  }> {
    const endpoint = `${API_ROUTES.v1.home}/storefront`;
    const response = await apiRequest<unknown>(endpoint);
    const record = response && typeof response === 'object' ? (response as { data?: Record<string, unknown> }).data : null;
    const partnerStores: PartnerStoreSummary[] = Array.isArray(record?.partnerStores)
      ? (record.partnerStores as Array<Record<string, unknown>>).map((row) => {
          const openStatusRaw = String(row.openStatus || '').trim();
          const openStatus: PartnerStoreSummary['openStatus'] =
            openStatusRaw === 'open' ||
            openStatusRaw === 'close_buffer' ||
            openStatusRaw === 'closed' ||
            openStatusRaw === 'unknown'
              ? openStatusRaw
              : undefined;
          return {
            storeId: String(row.storeId || ''),
            storeName: String(row.storeName || 'Store'),
            isDispatchHub: Boolean(row.isDispatchHub),
            storeType: String(row.storeType || 'other'),
            openStatus,
            isSelectableForImmediateShopping:
              typeof row.isSelectableForImmediateShopping === 'boolean'
                ? row.isSelectableForImmediateShopping
                : undefined
          };
        }).filter((row) => row.storeId)
      : [];
    return {
      allowGuestCheckout: Boolean(record?.allowGuestCheckout ?? record?.allow_guest_checkout ?? true),
      checkoutAvailable: Boolean(record?.checkoutAvailable ?? true),
      maintenanceMode: Boolean(record?.maintenanceMode),
      fulfillmentModel: record?.fulfillmentModel === 'hub_with_retail_backup'
        ? 'hub_with_retail_backup'
        : 'passthrough_shopper',
      ownStoreInventoryEnabled: Boolean(record?.ownStoreInventoryEnabled),
      shoppingCloseBufferMinutes: Number(record?.shoppingCloseBufferMinutes ?? 60),
      hubShoppingCloseBufferMinutes: Number(record?.hubShoppingCloseBufferMinutes ?? 0),
      partnerStores
    };
  }
};
